import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../../../../redux/ducks/Loading";
import { setRefreshWorkCostsTotals } from "../../../../../redux/ducks/ProjectManagement";
import { Table, Row, Col, Tooltip, Popover } from "antd";
import { EyeOutlined, WarningOutlined } from "@ant-design/icons";
import Button from "../../../../../components/functional_components/Button/PFButton";
import RdaDetailModal from "./RdaDetailModal";
import ProjectCostPlan from "../../../../../services/api/ProjectCostPlan";
import { formatNumber, showError, elaborateWCdata } from "../../../../../utils/utilities";
import { mapMonths } from "../../../../../utils/properties";
import { get, isEmpty, toLower, toNumber, lowerCase } from "lodash";

const TotalWCTable = (props) => {
  const tableRef = props.totalTableRef;
  const ExpTableRef = useRef(null);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const refreshWCTot = useSelector((state) => state.ProjectManagementDuck.refreshWorkCostTotals);
  const rdaJobRunning = useSelector((state) => get(state.ProjectManagementDuck, "rdaJobRunning", false));
  const runningCostJobRunning = useSelector((state) =>
    get(state.ProjectManagementDuck, "runningCostJobRunning", false)
  );
  const [state, setState] = useState({
    totalContentExpanded: [],
    columnsTotal: [],
    columnsExpanded: [],
    runningMonthNumber: 0,
    runningYearNumber: 0,
  });
  const [rdaDetailModalState, setRdaDetailModalState] = useState({ open: false, projectTaskId: null, serviceId: null });

  useEffect(() => {
    // The running month is the current month: it is always the month after open month
    const openMonthNumber = mapMonths[lowerCase(props.dataContentTotal[0].openMonth)];
    const runningMonthNumber = openMonthNumber + 1 > 12 ? 1 : openMonthNumber + 1;
    const runningYearNumber =
      openMonthNumber + 1 > 12
        ? toNumber(props.dataContentTotal[0].openYear) + 1
        : toNumber(props.dataContentTotal[0].openYear);
    setState((prevState) => ({ ...prevState, runningMonthNumber, runningYearNumber }));
  }, [props.dataContentTotal]);

  useEffect(() => {
    props.notifyWCTableOnExpandChange();
  }, [state.totalContentExpanded]);

  useEffect(() => {
    if (refreshWCTot === true) {
      columnsExpanded(props.dataContentTotal);
      dispatch(setRefreshWorkCostsTotals(false));
    }

    if (!isEmpty(props.dataContentTotal)) {
      columns(props.dataContentTotal);
    }

    if (tableRef.current && !isEmpty(props.dataContentTotal)) {
      const columnNode = Array.from(tableRef.current.querySelectorAll("th")).filter(
        (th) => th.textContent == props.dataContentTotal[0].openYear && th.classList.contains("ant-table-cell")
      );
      if (columnNode[0]) {
        tableRef.current.querySelector(".ant-table-content").scrollLeft = columnNode[0].offsetLeft;
      }
    }
    if (!isEmpty(ExpTableRef.current) && !isEmpty(props.dataContentTotal)) {
      const columnNodeExpanded = Array.from(tableRef.current.querySelectorAll("th")).filter(
        (th) => th.textContent == props.dataContentTotal[0].openYear && th.classList.contains("ant-table-cell")
      );
      if (columnNodeExpanded[0]) {
        columnNodeExpanded[0].scrollIntoView({ behavior: "smooth", block: "center" });
        tableRef.current.querySelectorAll(".ant-table-content").forEach((table) => {
          table.scrollLeft = columnNodeExpanded[0].offsetLeft;
        });
      }
    }
  }, [refreshWCTot, state.totalContentExpanded, tableRef.current]);

  useEffect(() => {
    columnsExpanded(props.dataContentTotal);
  }, [state.runningMonthNumber]);

  const columns = (allData) => {
    let objCol = [
      {
        title: "",
        width: 200,
        fixed: "left",
        render: () => t("projectEstimate.detail.tabs.costEstimation.table.totalProjects"),
      },
    ];
    if (!isEmpty(allData)) {
      allData[0].totalAmountValues.forEach((item, i) => {
        let childrenArr = [];
        Object.keys(item.columnValueMap).forEach((cvm) => {
          if (item.year === allData[0].openYear && cvm == allData[0].openMonth) {
            if (allData[0].closed) {
              childrenArr.push({
                title: t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`),
                width: 100,
                dataIndex: ["consAmountOpenMonth"],
                className: `table-value-right`,
                render: (amount) =>
                  formatNumber({ value: amount ? amount : 0, suffix: "EUR", lang: currentLanguage, decimalScale: 0 }),
              });
            } else {
              childrenArr.push({
                title: t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`) + " Plan",
                width: 100,
                dataIndex: ["totalAmountValues", i, "columnValueMap", cvm],
                className: `table-value-right`,
                render: (amount) =>
                  formatNumber({ value: amount ? amount : 0, suffix: "EUR", lang: currentLanguage, decimalScale: 0 }),
              });
              childrenArr.push({
                title: t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`) + " Act",
                width: 100,
                dataIndex: ["consAmountOpenMonth"],
                className: `table-value-right present`,
                render: (amount) =>
                  formatNumber({ value: amount ? amount : 0, suffix: "EUR", lang: currentLanguage, decimalScale: 0 }),
              });
            }
          } else {
            // Months
            childrenArr.push({
              title: t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`),
              width: 100,
              dataIndex: ["totalAmountValues", i, "columnValueMap", cvm],
              className: `table-value-right`,
              render: (amount) =>
                formatNumber({ value: amount ? amount : 0, suffix: "EUR", lang: currentLanguage, decimalScale: 0 }),
            });
          }

          if (item.year === state.runningYearNumber && mapMonths[toLower(cvm)] == state.runningMonthNumber) {
            childrenArr.push({
              title: t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`) + " Run",
              width: 100,
              dataIndex: ["runningAmount"],
              className: `table-value-right running` + (runningCostJobRunning ? " alert-currently-updating" : ""),
              render: (amount) => {
                return (
                  <Popover
                    trigger={["hover", "focus"]}
                    content={
                      runningCostJobRunning ? t("projectsManagement.planning.workCostsTab.alertCurrentlyUpdating") : ""
                    }
                    placement="right"
                    classNames={{
                      root: "tooltip-like-popover",
                    }}
                  >
                    <Row>
                      {formatNumber({
                        value: amount ? amount : 0,
                        suffix: "EUR",
                        lang: currentLanguage,
                        decimalScale: 0,
                      })}
                    </Row>
                  </Popover>
                );
              },
            });
          }
        });
        objCol.push({
          title: item.year,
          width: 100,
          className: "table-value-right",
          children: childrenArr,
        });
      });

      // Tot_rda ,Total, total-1 total budget
      objCol.push({
        title: t(`projectsManagement.planning.workCostsTab.${toLower("TOT_RDA")}`),
        width: 150,
        dataIndex: ["totalAmountMap", "TOT_RDA"],
        fixed: "right",
        className: "table-value-right highlighted" + (rdaJobRunning ? " alert-currently-updating" : ""),
        render: (amount, record) => {
          return (
            <Popover
              trigger={["hover", "focus"]}
              content={rdaJobRunning ? t("projectsManagement.planning.workCostsTab.alertCurrentlyUpdating") : ""}
              placement="right"
              classNames={{
                root: "tooltip-like-popover",
              }}
            >
              <Row>
                {formatNumber({
                  value: amount && amount > 0 ? amount : 0,
                  suffix: record.isHour ? "HOUR" : "EUR",
                  lang: currentLanguage,
                  decimalScale: 0,
                })}
              </Row>
            </Popover>
          );
        },
      });

      objCol.push({
        title: t(`projectsManagement.planning.workCostsTab.${toLower("TOT")}`),
        width: 100,
        dataIndex: ["totalAmountMap", "TOT"],
        fixed: "right",
        className: "table-value-right highlighted",
        render: (amount, record) =>
          formatNumber({
            value: amount ? amount : 0,
            suffix: record.isHour ? "HOUR" : "EUR",
            lang: currentLanguage,
            decimalScale: 0,
          }),
      });

      objCol.push({
        title: t(`projectsManagement.planning.workCostsTab.${toLower("TOT_PREV_MONTH")}`),
        width: 100,
        dataIndex: ["totalAmountMap", "TOT_PREV_MONTH"],
        fixed: "right",
        className: "table-value-right highlighted",
        render: (amount, record) =>
          formatNumber({
            value: amount ? amount : 0,
            suffix: record.isHour ? "HOUR" : "EUR",
            lang: currentLanguage,
            decimalScale: 0,
          }),
      });

      objCol.push({
        title: t(`projectsManagement.planning.workCostsTab.${toLower("TOT_BUDGET")}`),
        width: 100,
        dataIndex: ["totalAmountMap", "TOT_BUDGET"],
        fixed: "right",
        className: "table-value-right highlighted",
        render: (amount, record) =>
          formatNumber({
            value: amount ? amount : 0,
            suffix: record.isHour ? "HOUR" : "EUR",
            lang: currentLanguage,
            decimalScale: 0,
          }),
      });
    }
    setState((prevState) => ({ ...prevState, columnsTotal: objCol }));
  };

  const getRdaCellContent = (record) => {
    const rdaObject = record.rda ? record.rda : null;
    const value = rdaObject && rdaObject.rda && rdaObject.rda > 0 ? rdaObject.rda : 0;

    if (
      record.activity.includes("SERVMOI") ||
      record.activity.includes("SERVMOE") ||
      record.activity === "work" ||
      record.activity === "costs"
    ) {
      return "";
    }

    return (
      <Popover
        trigger={["hover", "focus"]}
        content={rdaJobRunning ? t("projectsManagement.planning.workCostsTab.alertCurrentlyUpdating") : ""}
        placement="right"
        classNames={{
          root: "tooltip-like-popover",
        }}
      >
        <Row gutter={4} align="middle" justify="space-between" style={{ paddingLeft: "2px" }}>
          <Button
            currentIcon={<EyeOutlined />}
            clickCallback={() =>
              setRdaDetailModalState({
                open: true,
                projectId: props.id,
                projectTaskId: record.projectTaskId,
                serviceId: record.activity,
              })
            }
          />
          <Col style={{ justifyItems: "center" }}>
            {rdaObject && rdaObject.rdaAlert && (
              <Tooltip title={t("projectsManagement.planning.rdaDetail.rdaAlert")} placement="top">
                <WarningOutlined style={{ color: "#ff0000", cursor: "pointer" }} />
              </Tooltip>
            )}
            {rdaObject && rdaObject.rdaExtraBudget && (
              <Tooltip title={t("projectsManagement.planning.rdaDetail.extraBudget")} placement="bottom">
                <div className="extraBudget">
                  <span>&euro;</span>
                </div>
              </Tooltip>
            )}
          </Col>
          <Col>
            {formatNumber({
              value,
              suffix: "EUR",
              lang: currentLanguage,
              decimalScale: 0,
            })}
          </Col>
        </Row>
      </Popover>
    );
  };

  const columnsExpanded = (allData) => {
    let objCol = [
      {
        title: t("projectsManagement.planning.workCostsTab.activityResource"),
        dataIndex: "activity",
        width: 247,
        fixed: "left",
        render: (activity) => {
          if (activity === "work") return t("projectsManagement.planning.workCostsTab.work");
          else if (activity === "costs") return t("projectsManagement.planning.workCostsTab.costs");
          return activity;
        },
      },
    ];

    allData[0].totalAmountValues.forEach((item, i) => {
      let childrenArr = [];
      Object.keys(item.columnValueMap).forEach((cvm) => {
        if (item.year === allData[0].openYear && cvm == allData[0].openMonth) {
          if (allData[0].closed) {
            childrenArr.push({
              title: t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`),
              width: 100,
              dataIndex: ["PRESENT"],
              className: `table-value-right`,
              render: (amount, record) =>
                formatNumber({
                  value: amount ? amount : 0,
                  suffix: record.isHour ? "HOUR" : "EUR",
                  lang: currentLanguage,
                  decimalScale: 0,
                }),
            });
          } else {
            childrenArr.push({
              title: t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`) + " Plan",
              width: 100,
              dataIndex: [i, "columnValueMap", cvm],
              className: `table-value-right`,
              render: (amount, record) =>
                formatNumber({
                  value: amount ? amount : 0,
                  suffix: record.isHour ? "HOUR" : "EUR",
                  lang: currentLanguage,
                  decimalScale: 0,
                }),
            });
            childrenArr.push({
              title: t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`) + " Act",
              width: 100,
              dataIndex: ["PRESENT"],
              className: `table-value-right present`,
              render: (amount, record) =>
                formatNumber({
                  value: amount ? amount : 0,
                  suffix: record.isHour ? "HOUR" : "EUR",
                  lang: currentLanguage,
                  decimalScale: 0,
                }),
            });
          }
        } else {
          // Months
          childrenArr.push({
            title: t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`),
            width: 100,
            dataIndex: [i, "columnValueMap", cvm],
            className: `table-value-right`,
            render: (amount, record) =>
              formatNumber({
                value: amount ? amount : 0,
                suffix: record.isHour ? "HOUR" : "EUR",
                lang: currentLanguage,
                decimalScale: 0,
              }),
          });
        }

        if (item.year === state.runningYearNumber && mapMonths[toLower(cvm)] == state.runningMonthNumber) {
          childrenArr.push({
            title: t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`) + " Run",
            width: 100,
            className: `table-value-right running` + (runningCostJobRunning ? " alert-currently-updating" : ""),
            render: (amount, record) => {
              return (
                <Popover
                  trigger={["hover", "focus"]}
                  content={
                    runningCostJobRunning ? t("projectsManagement.planning.workCostsTab.alertCurrentlyUpdating") : ""
                  }
                  placement="right"
                  classNames={{
                    root: "tooltip-like-popover",
                  }}
                >
                  <Row>
                    {formatNumber({
                      value: record.isHour ? record.runningQuantity || 0 : record.runningAmount || 0,
                      suffix: record.isHour ? "HOUR" : "EUR",
                      lang: currentLanguage,
                      decimalScale: 0,
                    })}
                  </Row>
                </Popover>
              );
            },
          });
        }
      });
      objCol.push({
        title: item.year,
        width: 100,
        className: "table-value-right",
        children: childrenArr,
      });
    });

    // Tot_rda, Total, total-1 total budget
    objCol.push({
      title: t(`projectsManagement.planning.workCostsTab.${toLower("TOT_RDA")}`),
      width: 150,
      dataIndex: ["totalMap", "TOT_RDA"],
      fixed: "right",
      className: "table-value-right highlighted" + (rdaJobRunning ? " alert-currently-updating" : ""),
      render: (_, record) => getRdaCellContent(record),
    });

    objCol.push({
      title: t(`projectsManagement.planning.workCostsTab.${toLower("TOT")}`),
      width: 100,
      dataIndex: ["totalMap", "TOT"],
      fixed: "right",
      className: "table-value-right highlighted",
      render: (amount, record) =>
        formatNumber({
          value: amount ? amount : 0,
          suffix: record.isHour ? "HOUR" : "EUR",
          lang: currentLanguage,
          decimalScale: 0,
        }),
    });

    objCol.push({
      title: t(`projectsManagement.planning.workCostsTab.${toLower("TOT_PREV_MONTH")}`),
      width: 100,
      dataIndex: ["totalMap", "TOT_PREV_MONTH"],
      fixed: "right",
      className: "table-value-right highlighted",
      render: (amount, record) =>
        formatNumber({
          value: amount ? amount : 0,
          suffix: record.isHour ? "HOUR" : "EUR",
          lang: currentLanguage,
          decimalScale: 0,
        }),
    });

    objCol.push({
      title: t(`projectsManagement.planning.workCostsTab.${toLower("TOT_BUDGET")}`),
      width: 100,
      dataIndex: ["totalMap", "TOT_BUDGET"],
      fixed: "right",
      className: "table-value-right highlighted",
      render: (amount, record) =>
        formatNumber({
          value: amount ? amount : 0,
          suffix: record.isHour ? "HOUR" : "EUR",
          lang: currentLanguage,
          decimalScale: 0,
        }),
    });
    setState((prevState) => ({ ...prevState, columnsExpanded: objCol }));
  };

  const expandedRowRender = () => {
    return (
      <Table
        ref={ExpTableRef}
        bordered
        dataSource={state.totalContentExpanded}
        columns={state.columnsExpanded}
        size="small"
        pagination={false}
        scroll={{ x: 1600 }}
        rowClassName={(record) => (record.isFather ? "table-secondary" : "")}
        className="noScroll expanded-row-wc-table"
      />
    );
  };

  const getExpandedRowDataTotal = async (expanded) => {
    if (expanded) {
      dispatch(setLoading(true));
      const wbs = await ProjectCostPlan.projectCostPlanTotal(props.id);
      if (!isEmpty(wbs) && !wbs.error && wbs.data) {
        const dataElaborate = elaborateWCdata(wbs, userInfo);
        setState((prevState) => ({ ...prevState, totalContentExpanded: dataElaborate.contentExpanded }));
      } else {
        showError(get(wbs, "error.messageCode", "ER000"));
      }
      dispatch(setLoading(false));
    } else {
      setState((prevState) => ({ ...prevState, totalContentExpanded: [] }));
    }
  };

  return (
    <>
      <div ref={tableRef}>
        <Table
          bordered
          dataSource={props.dataContentTotal}
          columns={state.columnsTotal}
          size="small"
          pagination={false}
          scroll={{ x: 1600 }}
          expandable={{ expandedRowRender, onExpand: (expanded) => getExpandedRowDataTotal(expanded) }}
          rowClassName="table-primary"
        />
      </div>
      <RdaDetailModal modalState={rdaDetailModalState} setModalState={setRdaDetailModalState} />
    </>
  );
};

export default TotalWCTable;
